import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
} from "chart.js";
import { Card, CardContent, Box, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShieldVirus } from "@fortawesome/free-solid-svg-icons";

import { getOverAllMitreGraphDetails as getOverAllMitreService} from "services/modules/soc/agent";

ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, PointElement);

const ScatterGraph = ({ endDate, startDate }) => {
  // State Variables
  const [dataSource, setDataSource] = useState([]);

  // Redux selectors
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData);
  
  // Fetches Mitre Graph Details based on group
  useEffect(() => {
    const fetchMitreGraph = async () => {
      if (selectedCompanyContainer?.soc_env && selectedCompanyContainer?.soc_group && endDate && startDate) {
        const response = await getOverAllMitreService({
          soc_env: selectedCompanyContainer.soc_env,
          group: selectedCompanyContainer.soc_group,
          end: endDate,
          start: startDate,
        });
        const tacticsData = response?.data || [];
        const mappedData = tacticsData.map((tactic) => ({
          key: tactic.key,
          techniques: tactic.techniques.buckets.map((tech) => ({
            key: tech.key,
            max_rule_level: tech.max_rule_level.value,
          })),
        }));
        setDataSource(mappedData);
      }
    };
    fetchMitreGraph();
  }, [selectedCompanyContainer?.soc_env, selectedCompanyContainer?.soc_group, endDate, startDate]);

  const staticTactics = [
    "",
    "Initial Access",
    "Execution",
    "Persistence",
    "Privilege Escalation",
    "Defense Evasion",
    "Credential Access",
    "Discovery",
    "Lateral Movement",
    "Collection",
    "Command & Control",
    "Exfiltration",
    "Impact",
  ];

  const scatterData = staticTactics.flatMap((tactic) => {
    const foundTactic = dataSource.find((t) => t.key === tactic);
    return foundTactic
      ? foundTactic.techniques.map((technique) => ({
          x: tactic,
          y: technique.max_rule_level,
          label: technique.key,
          ruleLabel:
            technique.max_rule_level <= 6
              ? "Low"
              : technique.max_rule_level <= 8
              ? "Medium"
              : technique.max_rule_level <= 11
              ? "High"
              : "Critical",
        }))
      : [
          {
            x: tactic,
            y: null,
            label: "No Data",
            ruleLabel: "N/A",
          },
        ];
  });

  const data = {
    datasets: [
      {
        label: "Techniques Scatter",
        data: scatterData,
        backgroundColor: scatterData.map((point) => {
          switch (point.ruleLabel) {
            case "Low":
              return "#4e4ece";
            case "Medium":
              return "orange";
            case "High":
              return "#fc659c";
            case "Critical":
              return "red";
            case "N/A":
              return "gray";
            default:
              return "blue";
          }
        }),
        pointRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const point = scatterData[context.dataIndex];
            return point.y !== null ? `${point.label}` : `${point.label}`;
          },
        },
      },
      legend: {
        display: true,
        position: "top",
        labels: {
          usePointStyle: true,
          generateLabels: () => [
            { text: "Critical", fillStyle: "red", hidden: false },
            { text: "High", fillStyle: "#fc659c", hidden: false },
            { text: "Medium", fillStyle: "orange", hidden: false },
          ],
        },
      },
    },
    scales: {
      x: {
        type: "category",
        labels: staticTactics,
        title: {
          display: true,
          text: "Tactics",
        },
      },
      y: {
        min: 0,
        max: 15,
        ticks: {
          stepSize: 1,
        },
        title: {
          display: true,
          text: "Severity",
        },
      },
    },
  };

  return (
    <Card sx={{ margin: "10px", height: "500px" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center">
            <FontAwesomeIcon icon={faShieldVirus} className="soc-icon"  />
            <Typography variant="h6">Mitre Attacks</Typography>
          </Box>
        </Box>
        <Box height="350px">
          <Scatter data={data} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ScatterGraph;
